import type { AreaId, IncapacityId, PersonId, SubAreaId } from '@shared/brand';
import type { DateRange } from '../value-objects/DateRange';
import type { IncapacityStatus } from '../vocabulary';
import type { Auditable } from './Organization';

/**
 * A medical leave certified by the IMSS (Mexico's social security institute).
 *
 * Two properties set this apart from every other absence and drive the design:
 *
 *  - It is counted in **natural** days, not business days. A seven-day medical
 *    leave covers the weekend; the subsidy is paid per calendar day. Using the
 *    business-day count here would understate the absence and misstate payroll.
 *  - It never touches the vacation balance. The days are covered by the IMSS
 *    subsidy, which is why `BalancePolicy` ignores this collection entirely.
 *
 * The certificate is health data. It is stored under a path only the owner and
 * their managers can read, and it is excluded from every export.
 */
export interface Incapacity extends Auditable {
  readonly id: IncapacityId;
  /** e.g. `INC-3402`. */
  readonly folio: string;
  readonly personId: PersonId;
  readonly personName: string;
  readonly areaId: AreaId;
  readonly subAreaId: SubAreaId | null;
  readonly subAreaName: string;
  /** Catalogue name copied at creation, for the same reason as permissions. */
  readonly kind: string;
  readonly range: DateRange;
  /** Natural days — see above. Frozen at registration. */
  readonly naturalDays: number;
  /** IMSS certificate folio, blank until the document arrives. */
  readonly imssFolio: string;
  /** Unidad de Medicina Familiar that issued the certificate. */
  readonly umf: string;
  /** How the subsidy applies, e.g. "Subsidio 60%". Copied from the catalogue. */
  readonly subsidy: string;
  /** Free text describing who absorbs the work. */
  readonly coverage: string;
  readonly certificatePath: string | null;
  readonly status: IncapacityStatus;
  readonly notes: string;
  readonly validatedByPersonId: PersonId | null;
  readonly validatedAt: Date | null;
  /** Set when HR returns the certificate as illegible or mismatched. */
  readonly observation: string | null;
}

/** A configurable kind of incapacity, from the runtime catalogue. */
export interface IncapacityKind {
  readonly id: string;
  readonly note: string;
  readonly subsidy: string;
  /**
   * Si esta ausencia la certifica el IMSS.
   *
   * Casi todas sí, y de ellas sale el folio, la unidad médica y el subsidio del
   * instituto. **La paternidad no**: la LFT la pone en el 132 fracción XXVII bis
   * como permiso con goce que paga el patrón, sin certificado ni folio ni subsidio.
   * Vive en este catálogo por decisión de producto —queda a discreción del líder
   * aprobarla—, y esta bandera es lo que impide que el flujo le exija tres papeles
   * que no existen.
   *
   * Sin ella, encender el módulo `imss` dejaría la paternidad imposible de
   * registrar: el servidor pide folio y un padre no tiene ninguno.
   */
  readonly imssCertified: boolean;
  readonly enabled: boolean;
}

export const DEFAULT_INCAPACITY_KINDS: readonly IncapacityKind[] = [
  {
    id: 'Enfermedad general',
    note: 'Subsidio 60% desde el 4º día',
    subsidy: 'Subsidio 60%',
    imssCertified: true,
    enabled: true,
  },
  {
    id: 'Riesgo de trabajo',
    note: 'Subsidio 100% desde el 1er día',
    subsidy: 'Subsidio 100%',
    imssCertified: true,
    enabled: true,
  },
  {
    id: 'Maternidad',
    note: '84 días naturales',
    subsidy: 'Subsidio 100%',
    imssCertified: true,
    enabled: true,
  },
  {
    id: 'Cuidado de familiar',
    note: 'Requiere dictamen del IMSS',
    subsidy: 'Subsidio 60%',
    imssCertified: true,
    enabled: true,
  },
  /*
   * Paternidad. Está aquí por decisión de producto y no porque el IMSS la
   * certifique: la LFT la da como permiso con goce que paga el patrón. Por eso
   * `imssCertified: false` — sin folio, sin unidad médica y sin subsidio del
   * instituto. Queda a discreción del líder aprobarla, que es lo que se pidió.
   */
  {
    id: 'Paternidad',
    note: '5 días hábiles con goce · LFT art. 132 XXVII bis',
    subsidy: 'Sin subsidio · lo paga el patrón',
    imssCertified: false,
    enabled: true,
  },
];

export const SUBSIDY_OPTIONS = ['Subsidio 60%', 'Subsidio 100%', 'Sin subsidio'] as const;

/** An incapacity may only be validated once it actually has a certificate. */
export const canValidateWithImss = (incapacity: Incapacity): boolean =>
  incapacity.status !== 'Validada' && incapacity.imssFolio.trim().length > 0;

export const isIncapacityOpen = (status: IncapacityStatus): boolean =>
  status === 'Por validar' || status === 'Observada' || status === 'Programada';

/**
 * Si quien la registró todavía puede retirarla.
 *
 * Aquí no hay regla de fecha, y esa es la diferencia con un permiso. Una
 * incapacidad es un registro, no un plan: se presenta después del hecho tan a
 * menudo como antes, así que exigir «sólo antes de que empiece» dejaría sin
 * retirar prácticamente cualquier certificado mal capturado.
 *
 * Lo que sí manda es el estado. Una `Validada` es un certificado que RR.HH.
 * aceptó, con consecuencias en nómina; deshacerlo no es un botón de
 * autoservicio. Para ésas el camino es que RR.HH. la observe —lo que la
 * devuelve a su dueño— y entonces sí se retira.
 */
export const isIncapacityCancelable = (
  incapacity: Pick<Incapacity, 'status'>,
): boolean =>
  incapacity.status === 'Por validar' ||
  incapacity.status === 'Observada' ||
  incapacity.status === 'Programada';
